import React, { useRef, useState } from "react";
import { RefreshCw, ArrowDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { haptic } from "@/lib/haptics";

const THRESHOLD = 70;

// Touch pull-to-refresh wrapper. Becomes the scroll container for its children.
export default function PullToRefresh({ onRefresh, className, children, testid }) {
  const ref = useRef(null);
  const startY = useRef(null);
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  const onTouchStart = (e) => {
    startY.current = ref.current && ref.current.scrollTop <= 0 ? e.touches[0].clientY : null;
  };
  const onTouchMove = (e) => {
    if (startY.current == null || refreshing) return;
    if (ref.current.scrollTop > 0) { startY.current = null; setPull(0); return; }
    const dy = e.touches[0].clientY - startY.current;
    if (dy > 0) setPull(Math.min(dy * 0.5, 90));
  };
  const onTouchEnd = async () => {
    if (pull >= THRESHOLD && !refreshing) {
      setRefreshing(true);
      setPull(0);
      try { await onRefresh?.(); } catch { /* ignore */ }
      haptic([10, 40, 10]);
      setTimeout(() => setRefreshing(false), 400);
    } else {
      setPull(0);
    }
    startY.current = null;
  };

  const ready = pull >= THRESHOLD;
  const indicatorH = refreshing ? 44 : pull;

  return (
    <div
      ref={ref}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      className={cn("overflow-y-auto", className)}
      data-testid={testid}
    >
      <div
        className="flex items-center justify-center overflow-hidden text-slate-400 transition-[height] duration-150"
        style={{ height: indicatorH }}
        data-testid="pull-refresh-indicator"
      >
        {refreshing ? (
          <span className="flex items-center gap-1.5 text-[11px] font-semibold"><RefreshCw size={14} className="animate-spin text-[#0070BA]" /> Refreshing…</span>
        ) : pull > 4 ? (
          <span className="flex items-center gap-1.5 text-[11px] font-semibold" style={{ color: ready ? "#0070BA" : "#94A3B8" }}>
            <ArrowDown size={14} className="transition-transform duration-150" style={{ transform: ready ? "rotate(180deg)" : "none" }} />
            {ready ? "Release to refresh" : "Pull to refresh"}
          </span>
        ) : null}
      </div>
      {children}
    </div>
  );
}
